import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import moment from "moment";
import { Col, Row, Select, Spin } from "antd";
import { CALL_API } from "../../Common/api";
import {
  ReloadOutlined,
  TruckOutlined,
  WifiOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  FieldTimeOutlined,
  StopOutlined,
} from "@ant-design/icons";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ButtonFilled } from "../../Reusablecomponents/UIButtons";
import { deriveVehicleState } from "../LiveTrack/nexusVehicleState";
import { ReactComponent as DashVeh } from "../../assets/images/Dash-Veh.svg";
import { ReactComponent as DashAct } from "../../assets/images/Dash-Act.svg";
import { ReactComponent as DashOnl } from "../../assets/images/Dash-Onl.svg";
import { ReactComponent as DashCompl } from "../../assets/images/Dash-Compl.svg";
import { ReactComponent as DashDelay } from "../../assets/images/Dash-Delay.svg";
import { ReactComponent as DashOff } from "../../assets/images/Dash-Off.svg";
import { ReactComponent as DashTopVeh } from "../../assets/images/Dash-TopVeh.svg";
import { ReactComponent as DashTopDriv } from "../../assets/images/Dash-TopDri.svg";
import { ReactComponent as DashTopCust } from "../../assets/images/Dash-TopCust.svg";

// ─── Design tokens (matches the approved mockups) ──────────────────────────
// ─── Mock data — TraKing-sourced widgets (Fleet Size/Online/Offline, Fleet
// Status, Top Vehicles/Drivers/Customers) stay mocked until it's confirmed
// those should come from TraKing at all. Everything else here is now wired
// to the TrackRight AI APIs (ai/trip/getAllTrip, ai/consignments/getAll,
// dash/totalDashData) further down. ──────────────────────────────────────
const FLEET_HEALTH_META = [
  { key: "Moving", color: "#2C963D" },
  { key: "Parked", color: "#3B82F6" },
  { key: "Idle", color: "#F97316" },
  { key: "Offline", color: "#EF4444" },
];

// Same status rules as Live menu > TrackRight AI tab (non-cargo)
const getLiveVehicleStatus = (item, offlineMins = 10) => {
  const { Lon, Lat, Speed = 0, Lock, Vehicle, RT } = item || {};
  if (!RT) return "Inactivated";
  if (Lock?.LS === 0) return "Locked";
  if (Lock?.LS === 1) return "Unlocked";
  const last = new Date(RT).getTime();
  if (isNaN(last)) return "Inactivated";
  if ((Date.now() - last) / 60000 > offlineMins) return "Offline";
  if (!Lon || !Lat) return "NotPositioned";
  if (Speed !== 0) return "Moving";
  const acc = Vehicle?.ACC ?? null;
  if (acc !== null && acc !== 0) return "Idle";
  if (acc === 0) return "Stationary";
  return "UnknownStatus";
};

const RANK_RANGES = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "7d", label: "Last 7 days" },
  { value: "thisMonth", label: "This Month" },
  { value: "lastMonth", label: "Last Month" },
];

const rangeDates = (range) => {
  const f = "YYYY-MM-DD";
  const now = dayjs();
  if (range === "yesterday") {
    const d = now.subtract(1, "day").format(f);
    return { from: d, to: d };
  }
  if (range === "7d")
    return { from: now.subtract(6, "day").format(f), to: now.format(f) };
  if (range === "thisMonth")
    return { from: now.startOf("month").format(f), to: now.format(f) };
  if (range === "lastMonth") {
    const m = now.subtract(1, "month");
    return {
      from: m.startOf("month").format(f),
      to: m.endOf("month").format(f),
    };
  }
  return { from: now.format(f), to: now.format(f) };
};

// Trip Distribution risk color, by count of unresolved KPI alerts for the
// consignor today — confirmed business rule: >=3 High risk (red),
// 2 Medium risk (orange), 1 Low risk (yellow), 0 No risk (green).
const kpiTileStyle = (n) => {
  if (n >= 3) return { bg: "#B91C1C", color: "#fff" }; // High
  if (n === 2) return { bg: "#F97316", color: "#fff" }; // Medium
  if (n === 1) return { bg: "#FCD34D", color: "#3d2f00" }; // Low
  return { bg: "#DCFCE7", color: "#2C963D" }; // No risk
};

// Fallback shown only until the real ai/trip/getAllTrip fetch resolves
const TRIP_TREND_FALLBACK = [
  { day: "2026-07-18", completed: 22, inProgress: 4 },
  { day: "2026-07-19", completed: 17, inProgress: 3 },
  { day: "2026-07-20", completed: 20, inProgress: 4 },
  { day: "2026-07-21", completed: 26, inProgress: 4 },
  { day: "2026-07-22", completed: 26, inProgress: 5 },
  { day: "2026-07-23", completed: 21, inProgress: 6 },
  { day: "2026-07-24", completed: 15, inProgress: 12 },
];

// Fallback shown only until the real 12-month fetch resolves
const TRIPS_OVER_TIME_FALLBACK = [
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
].map((m, i) => ({ month: m, value: 2800 + i * 90 + (i % 3 === 0 ? 120 : 0) }));

// Fallback shown only until the real 12-month fetch resolves
const DISTANCE_OVER_TIME_FALLBACK = [
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
].map((m, i) => ({ month: m, value: 165000 + i * 7500 }));

// ─── Styling ────────────────────────────────────────────────────────────────
const Wrap = styled.div`
  padding: 20px 24px 40px;
  background: #ffffff;
  min-height: 100%;
  border-radius: 27px;

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;
  }
  .page-header h1 {
    font-size: 24px;
    font-weight: 600;
    margin: 0;
    color: #1f2430;
  }
  .page-header .date-line {
    color: #5f6d8c;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
  }
  .date-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #2c963d;
    display: inline-block;
  }

  .card {
    background: #fff;
    border-radius: 8px;
    border: 1px solid #eceef2;
    padding: 18px;
  }

  .kpi-strip {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 14px;
    margin-bottom: 18px;
  }
  @media (max-width: 1200px) {
    .kpi-strip {
      grid-template-columns: repeat(3, 1fr);
    }
  }
  @media (max-width: 640px) {
    .kpi-strip {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  .kpi-card .kpi-icon {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: #f5f6f8;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #5f6d8c;
    margin-bottom: 10px;
    font-size: 16px;
  }
  .kpi-card .kpi-value {
    font-size: 26px;
    font-weight: 700;
    color: #1f2430;
    line-height: 1.1;
  }
  .kpi-card .kpi-label {
    color: #5f6d8c;
    font-size: 13px;
    margin: 4px 0 8px;
  }
  .kpi-card .kpi-sub {
    font-size: 12px;
    font-weight: 500;
  }
  .kpi-sub.up {
    color: #2c963d;
  }
  .kpi-sub.down {
    color: #e5484d;
  }
  .kpi-sub.flat {
    color: #98a1b5;
  }

  .legend-row {
    display: flex;
    flex-wrap: wrap;
    gap: 18px;
    background: #fff;
    border: 1px solid #eceef2;
    border-radius: 12px;
    padding: 12px 18px;
    margin-bottom: 18px;
    font-size: 13px;
    color: #3a3f4b;
  }
  .legend-dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    display: inline-block;
    margin-right: 6px;
  }

  .heatmap-grid {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 12px;
    margin-bottom: 24px;
  }
  @media (max-width: 1300px) {
    .heatmap-grid {
      grid-template-columns: repeat(4, 1fr);
    }
  }
  @media (max-width: 700px) {
    .heatmap-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  .heat-tile {
    border-radius: 12px;
    padding: 16px 14px;
    min-height: 96px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: relative;
  }
  .heat-tile .score {
    font-size: 24px;
    font-weight: 700;
  }
  .heat-tile .name {
    font-size: 13px;
    font-weight: 500;
    line-height: 1.25;
  }
  .heat-tile .badge {
    position: absolute;
    top: 10px;
    right: 10px;
    background: #00000033;
    border-radius: 9999px;
    width: 8px;
    height: 8px;
    font-size: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
  }

  .section-title {
    font-size: 14px;
    font-weight: 700;
    color: #363636;
    margin-bottom: 4px;
  }
  .group-title {
    font-size: 18px;
    font-weight: 700;
    color: #363636;
    margin: 26px 0 14px;
  }
  .section-sub {
    font-size: 14px;
    color: #64748b;
    margin-bottom: 14px;
  }

  .fleet-health-legend {
    margin-top: 12px;
  }
  .fh-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    padding: 8px 0;
    color: #363636;
  }
  .fh-row .fh-nums {
    display: flex;
    align-items: center;
  }
  .fh-row .fh-count {
    min-width: 40px;
    text-align: right;
    font-weight: 400;
    sont-size: 14px;
    color: #1e293b;
  }
  .fh-row .fh-pct {
    min-width: 62px;
    text-align: right;
    color: #5f6d8c;
  }
  .fh-row .legend-dot {
    width: 10px;
    height: 10px;
    margin-right: 0;
  }
  .fh-row .fh-name {
    display: flex;
    align-items: center;
    gap: 14px;
    font-weight: 600;
  }
  .util-bar {
    margin-top: 14px;
  }
  .util-bar .bar-bg {
    background: #eceef2;
    border-radius: 6px;
    height: 8px;
    margin-top: 6px;
    overflow: hidden;
  }
  .util-bar .bar-fill {
    background: #2c963d;
    height: 100%;
    border-radius: 6px;
  }

  .trip-perf-stats {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 10px;
    margin-bottom: 16px;
  }
  @media (max-width: 900px) {
    .trip-perf-stats {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  .stat-tile {
    background: #f5f6f8;
    border-radius: 10px;
    padding: 10px 12px;
  }
  .stat-tile .stat-value {
    font-size: 17px;
    font-weight: 700;
    color: #1f2430;
  }
  .stat-tile .stat-label {
    font-size: 11px;
    color: #98a1b5;
  }

  .range-toggle {
    display: inline-flex;
    border: 1px solid #eceef2;
    border-radius: 8px;
    overflow: hidden;
  }
  .range-toggle button {
    border: none;
    background: #fff;
    padding: 6px 14px;
    font-size: 12px;
    cursor: pointer;
    color: #5f6d8c;
    outline: none;
  }
  .range-toggle button:focus,
  .range-toggle button:focus-visible {
    outline: none;
    box-shadow: none;
  }
  .range-toggle button.active {
    background: #f0f4ff;
    color: #2c963d;
    font-weight: 600;
  }

  .attention-item {
    background: #fdf8f3;
    border-radius: 10px;
    padding: 12px 14px;
    margin-bottom: 12px;
  }
  .attention-item:last-child {
    margin-bottom: 0;
  }
  .attention-item-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }
  .attention-level {
    font-size: 14px;
    font-weight: 600;
    color: #e08e1d;
  }
  .attention-desc {
    font-size: 13px;
    color: #5f6d8c;
    margin-top: 4px;
    line-height: 1.4;
  }
  .attention-view {
    font-size: 12px;
    color: #2c963d;
    font-weight: 500;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .leaderboard-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 7px 0;
    font-size: 13px;
    color: #3a3f4b;
  }
  .leaderboard-row .rank {
    color: #98a1b5;
    width: 20px;
    display: inline-block;
  }
  .leaderboard-row .trips {
    color: #98a1b5;
  }

  .trend-badge {
    font-size: 12px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 20px;
  }
  .trend-badge.up {
    background: #e8ffec;
    color: #2c963d;
  }
  .trend-badge.down {
    background: #ffece8;
    color: #e5484d;
  }
`;

const formatDuration = (mins) => {
  const total = Math.max(0, Math.round(mins || 0));
  return `${Math.floor(total / 60)}h ${total % 60}m`;
};

const pctChange = (current, previous) =>
  previous ? ((current - previous) / previous) * 100 : 0;

const NexusDashboardAI = () => {
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state?.authTracking?.userInfo);
  // Accounts linked to a TraKing company, and MCID 59 (the Nexus operator,
  // which is both a Nexus and a TrackRight AI account), read trips and KPI
  // alerts from the Nexus tables (nexus/dashboardstats) and vehicles from
  // TraKing (nexus/nexuslive — every company's vehicles for the operator).
  const isNexus =
    Boolean(userInfo?.NexusCompId) || Number(userInfo?.MCID) === 59;
  const [tripRange, setTripRange] = useState("7d");
  const [fleet, setFleet] = useState({
    total: 0,
    Moving: 0,
    Parked: 0,
    Idle: 0,
    Offline: 0,
  });
  const [rankRange, setRankRange] = useState({
    vehicles: "today",
    drivers: "today",
    customers: "today",
  });
  const [rankRows, setRankRows] = useState({});
  const [fleetLoading, setFleetLoading] = useState(true);

  // ─── Real data from TrackRight AI (ai/trip/getAllTrip, ai/consignments/getAll,
  // dash/totalDashData, aiReport/kpi/get_Kpialert_Table) — everything the
  // business confirmed comes from TrackRight AI, not Nexus and not TraKing. ──
  const [kpiToday, setKpiToday] = useState({
    activeTrips: 0,
    completedTripsToday: 0,
  });
  const [tripPerf, setTripPerf] = useState({
    total: 0,
    completed: 0,
    inProgress: 0,
    avgDurationMins: 0,
    daily: [],
  });
  const [attention, setAttention] = useState({
    items: [],
    total: 0,
    delayed: 0,
    kpiByCustomer: {},
  });
  const [customerVolume, setCustomerVolume] = useState([]);
  const [businessTrends, setBusinessTrends] = useState({
    trips: [],
    distance: [],
  });

  const fetchKpiToday = async (mcId) => {
    const todayStr = dayjs().format("YYYY-MM-DD");
    try {
      const { data } = await CALL_API("POST", "ai/trip/getAllTrip", {
        mcId,
        dateFrom: todayStr,
        dateTo: todayStr,
        offset: 1,
        perPage: 1,
      });
      setKpiToday({
        activeTrips: data?.inProgressCount || 0,
        completedTripsToday: data?.completedCount || 0,
      });
    } catch (err) {
      console.error("[NexusDashboardAI] KPI fetch error:", err);
    }
  };

  const fetchTripPerformance = async (mcId, range) => {
    if (isNexus) return fetchNexusTripPerformance(range);
    const days = range === "30d" ? 30 : range === "today" ? 1 : 7;
    const dateFrom = dayjs()
      .subtract(days - 1, "day")
      .format("YYYY-MM-DD");
    const dateTo = dayjs().format("YYYY-MM-DD");
    try {
      const { data } = await CALL_API("POST", "ai/trip/getAllTrip", {
        mcId,
        dateFrom,
        dateTo,
        offset: 1,
        perPage: 500,
      });
      const rows = Array.isArray(data?.data) ? data.data : [];

      const dayBuckets = {};
      for (let i = 0; i < days; i += 1) {
        const key = dayjs()
          .subtract(days - 1 - i, "day")
          .format("YYYY-MM-DD");
        dayBuckets[key] = { day: key, completed: 0, inProgress: 0 };
      }
      rows.forEach((r) => {
        const key = dayjs(r.Started || r.PickUpDateTime).format("YYYY-MM-DD");
        if (!dayBuckets[key]) return;
        if (r.STATUS === 4) dayBuckets[key].completed += 1;
        else if (r.STATUS === 3) dayBuckets[key].inProgress += 1;
      });

      const completedRows = rows.filter((r) => r.STATUS === 4);

      // Avg duration comes from one aggregate call (AvgTripTime, minutes)
      const { data: trends } = await CALL_API(
        "POST",
        "ai/trip/dashboardTrends",
        {
          mcId,
          dateFrom,
          dateTo,
          monthsFrom: dateFrom,
          monthsTo: dateTo,
        },
      );
      const avgDurationMins = Number(trends?.avgTripMinutes) || 0;

      setTripPerf({
        total: data?.totalCount ?? rows.length,
        completed: data?.completedCount ?? completedRows.length,
        inProgress:
          data?.inProgressCount ?? rows.filter((r) => r.STATUS === 3).length,
        avgDurationMins,
        daily: Object.values(dayBuckets),
      });
    } catch (err) {
      console.error("[NexusDashboardAI] Trip performance fetch error:", err);
    }
  };

  // Attention Required = the top 3 unresolved AI KPI alerts triggered today
  // (same data source as the KPI Report page's alert table).
  const fetchAttentionRequired = async (mcId) => {
    const todayStr = dayjs().format("YYYY-MM-DD");
    try {
      const { data } = await CALL_API(
        "POST",
        `aiReport/kpi/get_Kpialert_Table?MCID=${mcId}&startDate=${todayStr}&endDate=${todayStr}&page=1&PageSize=500`,
        {
          code: 0,
          FNAME: "",
          CustName: "",
          Shipper: "",
          Name: "",
          REGNO: "",
          Status: "0", // unresolved only
          ConsignId: "",
          ShipmentType: 0,
        },
      );
      const rows = Array.isArray(data?.KPIAlertTable) ? data.KPIAlertTable : [];
      const sorted = [...rows].sort((a, b) =>
        (b.CRON || "").localeCompare(a.CRON || ""),
      );
      const kpiByCustomer = {};
      rows.forEach((r) => {
        const name = r.CustName || "Unknown";
        kpiByCustomer[name] = (kpiByCustomer[name] || 0) + 1;
      });
      setAttention({
        items: sorted.slice(0, 3),
        total: data?.totalCount ?? rows.length,
        delayed: rows.filter((r) => /delay/i.test(r.NAME || "")).length,
        kpiByCustomer,
      });
    } catch (err) {
      console.error("[NexusDashboardAI] Attention Required fetch error:", err);
    }
  };

  // Customers to show as "Trip Distribution" tiles, scoped to TODAY's
  // assigned consignments (trip count per customer). Each tile's risk color
  // comes from attention.kpiByCustomer (today's unresolved KPI alert count)
  // — see kpiTileStyle for the confirmed business rule: >=3 High, 2 Medium,
  // 1 Low, 0 No risk.
  const fetchCustomerVolume = async (mcId) => {
    const todayStr = dayjs().format("YYYY-MM-DD");
    try {
      const { data } = await CALL_API("POST", "ai/consignments/getAll", {
        mcId,
        status: "2",
        dateFrom: todayStr,
        dateTo: todayStr,
        page: 1,
        pageSize: 200,
      });
      const rows = Array.isArray(data?.data) ? data.data : [];
      const counts = {};
      rows.forEach((r) => {
        const name = r.CustName || "Unknown";
        counts[name] = (counts[name] || 0) + 1;
      });
      const list = Object.entries(counts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);
      setCustomerVolume(list);
    } catch (err) {
      console.error("[NexusDashboardAI] Customer volume fetch error:", err);
    }
  };

  // 12-month completed trips + GPS (telematics) distance in a single call
  const fetchBusinessTrends = async (mcId) => {
    try {
      const months = Array.from({ length: 12 }).map((_, i) =>
        dayjs().subtract(11 - i, "month"),
      );
      const monthsFrom = months[0].startOf("month").format("YYYY-MM-DD");
      const monthsTo = months[11].endOf("month").format("YYYY-MM-DD");
      const { data } = await CALL_API("POST", "ai/trip/dashboardTrends", {
        mcId,
        dateFrom: monthsFrom,
        dateTo: monthsTo,
        monthsFrom,
        monthsTo,
      });
      const byMonth = {};
      (data?.monthly || []).forEach((r) => {
        byMonth[`${r.yr}-${r.mo}`] = r;
      });
      const series = months.map((m) => {
        const r = byMonth[`${m.year()}-${m.month() + 1}`];
        return {
          month: m.format("MMM"),
          trips: Number(r?.trips) || 0,
          distance: Math.round(Number(r?.distance) || 0),
        };
      });
      setBusinessTrends({
        trips: series.map((r) => ({ month: r.month, value: r.trips })),
        distance: series.map((r) => ({ month: r.month, value: r.distance })),
      });
    } catch (err) {
      console.error("[NexusDashboardAI] Business trends fetch error:", err);
    }
  };

  // Fleet data = same source/logic as Live menu > TrackRight AI tab (GP2000 devices)
  const fetchFleet = async () => {
    setFleetLoading(true);
    try {
      const { data: assets } = await CALL_API(
        "POST",
        "ai/trackinglink/allcompanyAssetList",
        {
          FAgentGUID: userInfo?.ECTSCompId || "",
        },
      );
      const guids = {};
      const walk = (nodes) =>
        (nodes || []).forEach((n) => {
          if (n.FChild?.length) walk(n.FChild);
          if (n.FNType === 2 && n.FCar?.TName === "GP2000" && n.FCar.GUID) {
            guids[n.FCar.ID] = n.FCar.GUID;
          }
        });
      walk(assets?.data);
      const ids = Object.keys(guids);
      const guidList = ids.map((id) => guids[id]);
      // Chunks of 50 are independent, so fetch them in parallel
      const chunks = [];
      for (let i = 0; i < guidList.length; i += 50) {
        chunks.push(guidList.slice(i, i + 50).join(","));
      }
      const responses = await Promise.all(
        chunks.map((assetGUIDs) =>
          CALL_API("POST", "ai/trackinglink/MonitorDevice", { assetGUIDs }),
        ),
      );
      const monitor = responses.flatMap(({ data: res }) => res?.data || []);
      const counts = { Moving: 0, Parked: 0, Idle: 0, Offline: 0 };
      const seen = new Set();
      monitor.forEach((m) => {
        if (!guids[m.ID] || seen.has(String(m.ID))) return;
        seen.add(String(m.ID));
        const st = getLiveVehicleStatus(m);
        if (st === "Moving") counts.Moving += 1;
        else if (st === "Idle") counts.Idle += 1;
        else if (st === "Stationary") counts.Parked += 1;
        else if (st === "Offline" || st === "Inactivated") counts.Offline += 1;
      });
      // Vehicles in the hierarchy with no monitor record count as offline (as in Live)
      counts.Offline += ids.filter((id) => !seen.has(String(id))).length;
      setFleet({ total: ids.length, ...counts });
    } catch (err) {
      console.error("[NexusDashboardAI] Fleet fetch error:", err);
    } finally {
      setFleetLoading(false);
    }
  };

  // One call covers all three leaderboards for a given range.
  const fetchRankRows = async (mcId, range) => {
    if (isNexus) return fetchNexusTopStats(range);
    const { from, to } = rangeDates(range);
    try {
      const { data } = await CALL_API("POST", "ai/trip/topStats", {
        mcId,
        dateFrom: from,
        dateTo: to,
        top: 7,
      });
      setRankRows((prev) => ({
        ...prev,
        [range]: {
          vehicles: Array.isArray(data?.vehicles)
            ? data.vehicles.map((v) => ({ name: v.name, trips: v.trips }))
            : [],
          drivers: Array.isArray(data?.drivers)
            ? data.drivers.map((v) => ({ name: v.name, trips: v.trips }))
            : [],
          customers: Array.isArray(data?.customers)
            ? data.customers.map((v) => ({ name: v.name, trips: v.trips }))
            : [],
        },
      }));
    } catch (err) {
      console.error("[NexusDashboardAI] Top stats fetch error:", err);
    }
  };

  // ─── Nexus (TraKing-linked accounts) ─────────────────────────────────────
  // KPI strip, Trip Distribution tiles and the 12-month trips series in one call.
  const fetchNexusOverview = async () => {
    try {
      const months = Array.from({ length: 12 }).map((_, i) =>
        dayjs().subtract(11 - i, "month"),
      );
      const { data } = await CALL_API("POST", "nexus/dashboardstats", {
        section: "overview",
        today: dayjs().format("YYYY-MM-DD"),
        monthsFrom: months[0].startOf("month").format("YYYY-MM-DD"),
        monthsTo: months[11].endOf("month").format("YYYY-MM-DD"),
      });
      setKpiToday({
        activeTrips: Number(data?.inProgress) || 0,
        completedTripsToday: Number(data?.completed) || 0,
      });
      setCustomerVolume(
        (data?.customers || []).map((c) => ({
          name: c.name,
          count: Number(c.count) || 0,
        })),
      );
      const byMonth = {};
      (data?.monthly || []).forEach((r) => {
        byMonth[`${r.yr}-${r.mo}`] = Number(r.trips) || 0;
      });
      // Distance per month = sum of the completed trips' gpskms (null when the
      // tenant DB doesn't have the column yet, which hides the chart)
      const distanceByMonth = {};
      (data?.distanceMonthly || []).forEach((r) => {
        distanceByMonth[`${r.yr}-${r.mo}`] = Number(r.distance) || 0;
      });
      setBusinessTrends({
        trips: months.map((m) => ({
          month: m.format("MMM"),
          value: byMonth[`${m.year()}-${m.month() + 1}`] || 0,
        })),
        distance: data?.distanceMonthly
          ? months.map((m) => ({
              month: m.format("MMM"),
              value: Math.round(distanceByMonth[`${m.year()}-${m.month() + 1}`] || 0),
            }))
          : [],
      });
    } catch (err) {
      console.error("[NexusDashboardAI] Nexus overview fetch error:", err);
    }
  };

  // Today's unresolved KPI alerts: Attention Required, Delayed Trips and the
  // risk colour of the Trip Distribution tiles
  const fetchNexusKpi = async () => {
    try {
      const { data } = await CALL_API("POST", "nexus/dashboardstats", {
        section: "kpi",
        today: dayjs().format("YYYY-MM-DD"),
      });
      setAttention({
        items: data?.items || [],
        total: Number(data?.total) || 0,
        delayed: Number(data?.delayed) || 0,
        kpiByCustomer: data?.byCustomer || {},
      });
    } catch (err) {
      console.error("[NexusDashboardAI] Nexus KPI fetch error:", err);
    }
  };

  const fetchNexusTripPerformance = async (range) => {
    const days = range === "30d" ? 30 : range === "today" ? 1 : 7;
    try {
      const { data } = await CALL_API("POST", "nexus/dashboardstats", {
        section: "tripperformance",
        dateFrom: dayjs()
          .subtract(days - 1, "day")
          .format("YYYY-MM-DD"),
        dateTo: dayjs().format("YYYY-MM-DD"),
      });
      const byDay = {};
      (data?.daily || []).forEach((r) => {
        byDay[r.day] = r;
      });
      const daily = Array.from({ length: days }).map((_, i) => {
        const key = dayjs()
          .subtract(days - 1 - i, "day")
          .format("YYYY-MM-DD");
        return {
          day: key,
          completed: Number(byDay[key]?.completed) || 0,
          inProgress: Number(byDay[key]?.inProgress) || 0,
        };
      });
      const sum = (k) =>
        (data?.daily || []).reduce((t, r) => t + (Number(r[k]) || 0), 0);
      setTripPerf({
        total: sum("total"),
        completed: sum("completed"),
        inProgress: sum("inProgress"),
        avgDurationMins: Number(data?.avgMinutes) || 0,
        daily,
      });
    } catch (err) {
      console.error("[NexusDashboardAI] Nexus trip performance error:", err);
    }
  };

  const fetchNexusTopStats = async (range) => {
    const { from, to } = rangeDates(range);
    try {
      const { data } = await CALL_API("POST", "nexus/dashboardstats", {
        section: "topstats",
        dateFrom: from,
        dateTo: to,
        top: 7,
      });
      const rows = (list) =>
        Array.isArray(list)
          ? list.map((v) => ({ name: v.name, trips: Number(v.trips) || 0 }))
          : [];
      setRankRows((prev) => ({
        ...prev,
        [range]: {
          vehicles: rows(data?.vehicles),
          drivers: rows(data?.drivers),
          customers: rows(data?.customers),
        },
      }));
    } catch (err) {
      console.error("[NexusDashboardAI] Nexus top stats error:", err);
    }
  };

  // Fleet status from TraKing's live monitoring data (company-scoped by the backend)
  const fetchNexusFleet = async () => {
    setFleetLoading(true);
    try {
      const { data } = await CALL_API("GET", "nexus/nexuslive");
      const rows = data?.monitoringData || [];
      const counts = { Moving: 0, Parked: 0, Idle: 0, Offline: 0 };
      rows.forEach((v) => {
        const st = deriveVehicleState(v);
        if (st === "moving") counts.Moving += 1;
        else if (st === "idle") counts.Idle += 1;
        else if (st === "parked") counts.Parked += 1;
        else counts.Offline += 1;
      });
      setFleet({ total: rows.length, ...counts });
    } catch (err) {
      console.error("[NexusDashboardAI] Nexus fleet fetch error:", err);
    } finally {
      setFleetLoading(false);
    }
  };

  const [refreshing, setRefreshing] = useState(false);

  const refreshAll = async () => {
    if (!userInfo?.MCID || refreshing) return;
    setRefreshing(true);
    try {
      await Promise.all([
        fetchTripPerformance(userInfo.MCID, tripRange),
        ...(isNexus
          ? [fetchNexusOverview(), fetchNexusFleet(), fetchNexusKpi()]
          : [
              fetchKpiToday(userInfo.MCID),
              fetchCustomerVolume(userInfo.MCID),
              fetchBusinessTrends(userInfo.MCID),
              fetchAttentionRequired(userInfo.MCID),
              fetchFleet(),
            ]),
        ...rangesInUse.map((r) => fetchRankRows(userInfo.MCID, r)),
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!userInfo?.MCID) return;
    // Light, above-the-fold calls first; the 24-request 12-month trend burst
    // starts afterwards so it doesn't saturate the browser's connection pool
    // and delay the KPI strip / fleet / attention widgets.
    if (isNexus) {
      fetchNexusOverview();
      fetchNexusFleet();
      fetchNexusKpi();
      return;
    }
    Promise.allSettled([
      fetchKpiToday(userInfo.MCID),
      fetchCustomerVolume(userInfo.MCID),
      fetchAttentionRequired(userInfo.MCID),
      fetchFleet(),
    ]).then(() => fetchBusinessTrends(userInfo.MCID));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.MCID, isNexus]);

  useEffect(() => {
    if (!userInfo?.MCID) return;
    fetchTripPerformance(userInfo.MCID, tripRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.MCID, tripRange]);

  const rangesInUse = [...new Set(Object.values(rankRange))];
  useEffect(() => {
    if (!userInfo?.MCID) return;
    rangesInUse.forEach((r) => {
      if (!rankRows[r]) fetchRankRows(userInfo.MCID, r);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.MCID, rangesInUse.join(","), rankRows]);

  const topN = (range, panelId) => rankRows[range]?.[panelId] || [];

  const KPIS = [
    { label: "Fleet Size", value: fleet.total, icon: <DashVeh />, fleet: true },
    {
      label: "Vehicles Online",
      fleet: true,
      value: fleet.total - fleet.Offline,
      icon: <DashOnl />,
    },
    {
      label: "Active Trips",
      value: kpiToday.activeTrips,
      icon: <DashAct />,
    },
    {
      label: "Completed Trips Today",
      value: kpiToday.completedTripsToday,
      icon: <DashCompl />,
    },
    { label: "Delayed Trips", value: attention.delayed, icon: <DashDelay /> },
    {
      label: "Offline Vehicles",
      value: fleet.Offline,
      icon: <DashOff />,
      fleet: true,
    },
  ];

  const heatmapCustomers = customerVolume.map((c) => ({
    ...c,
    kpis: attention.kpiByCustomer[c.name] || 0,
  }));
  const totalTripsCount = heatmapCustomers.reduce(
    (sum, c) => sum + (c.count || 0),
    0,
  );
  const tripTrendData = tripPerf.daily.length
    ? tripPerf.daily
    : TRIP_TREND_FALLBACK;
  const tripsSeries = businessTrends.trips.length
    ? businessTrends.trips
    : TRIPS_OVER_TIME_FALLBACK;
  const distanceSeries = businessTrends.distance.length
    ? businessTrends.distance
    : DISTANCE_OVER_TIME_FALLBACK;
  const lastTrips = tripsSeries[tripsSeries.length - 1];
  const prevTrips = tripsSeries[tripsSeries.length - 2];
  const tripsPct = pctChange(lastTrips?.value || 0, prevTrips?.value || 0);
  // AI accounts always show it; Nexus accounts once the trip distance totals are in
  const showDistanceChart = !isNexus || businessTrends.distance.length > 0;
  const lastDistance = distanceSeries[distanceSeries.length - 1];
  const prevDistance = distanceSeries[distanceSeries.length - 2];
  const distancePct = pctChange(
    lastDistance?.value || 0,
    prevDistance?.value || 0,
  );

  const attentionDisplay = attention.items.map((r) => ({
    level: r.NAME || "Alert",
    title:
      r.MESSAGE ||
      `${r.CustName || ""} ${r.FName || ""}`.trim() ||
      `${r.tripcode || ""} ${r.vehiclename || ""}`.trim() ||
      "-",
    sub: r.CRON ? dayjs(r.CRON).format("h:mm A") : "",
    navName: r.NAME,
  }));
  const attentionCount = attention.total;

  // navName: filter by KPI name (Attention list); consignor: filter by consignor
  // (Trip Distribution tile count)
  const goToKpiReport = (navName, consignor) => {
    navigate(isNexus ? "/nexusKpiReport" : "/AIkpi report", {
      state: {
        date: dayjs(),
        label: "Unresolved",
        ...(navName ? { Name: navName } : {}),
        ...(consignor ? { consignor } : {}),
      },
    });
  };

  // East Africa Time (UTC+3) — same convention as the rest of the Nexus
  // pages (see formatNairobiTime), so the greeting/date match the office's
  // local time rather than the viewer's browser timezone.
  const { today, greeting } = useMemo(() => {
    const eatNow = moment().utcOffset(180);
    const hour = eatNow.hour();
    return {
      today: eatNow.format("D MMMM YYYY"),
      greeting:
        hour < 12
          ? "Good Morning"
          : hour < 17
            ? "Good Afternoon"
            : "Good Evening",
    };
  }, []);

  const FLEET_HEALTH = FLEET_HEALTH_META.map((f) => ({
    ...f,
    value: fleet[f.key],
    pct: fleet.total ? Math.round((fleet[f.key] / fleet.total) * 1000) / 10 : 0,
  }));
  const totalVehicles = fleet.total;
  const utilizationRate = 65;

  return (
    <Wrap>
      <div className="page-header">
        <div>
          <h1>
            {greeting},{" "}
            {[userInfo?.firstName, userInfo?.lastName]
              .filter(Boolean)
              .join(" ") || "there"}{" "}
          </h1>
          <div className="date-line">
            <span className="date-dot" />
            {today}
          </div>
        </div>
        <ButtonFilled
          onClick={refreshAll}
          disabled={refreshing}
          style={{ display: "flex", alignItems: "center", gap: 8 }}
        >
          <ReloadOutlined spin={refreshing} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </ButtonFilled>
      </div>

      {/* KPI strip */}
      <div className="kpi-strip">
        {KPIS.map((k) => (
          <div className="card kpi-card" key={k.label}>
            <div className="kpi-icon">{k.icon}</div>
            <div className="kpi-value">
              {k.fleet && fleetLoading ? <Spin size="small" /> : k.value}
            </div>
            <div className="kpi-label">{k.label}</div>
            {k.sub && (
              <div className={`kpi-sub ${k.trend}`}>
                {k.trend === "up" ? "▲ " : k.trend === "down" ? "▼ " : "– "}
                {k.sub}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="section-title" style={{ marginTop: 4 }}>
        Trip Distribution
      </div>
      <br />
      <div className="heatmap-grid">
        {/* <div
          className="heat-tile"
          style={{ background: "#C0281C", color: "#fff" }}
        >
          <span
            className="badge"
            style={{
              background: "#7F1D1D",
              color: "#fff",
              width: "auto",
              height: "auto",
              padding: "2px 8px",
            }}
          >
            KPI
          </span>
          <div className="score">{totalTripsCount}</div>
          <div className="name">Trips</div>
        </div> */}
        {heatmapCustomers.map((c) => {
          const t = kpiTileStyle(c.kpis);
          return (
            <div
              key={c.name}
              className="heat-tile"
              style={{ background: t.bg, color: t.color }}
            >
              {c.kpis > 0 && (
                <span
                  className="badge"
                  role="button"
                  title={`View ${c.name}'s KPI alerts`}
                  onClick={() => goToKpiReport(undefined, c.name)}
                  style={{
                    background: "rgba(0,0,0,0.35)",
                    color: "#fff",
                    width: "auto",
                    height: "auto",
                    padding: "1px 7px",
                    cursor: "pointer",
                  }}
                >
                  {c.kpis}
                </span>
              )}
              <div className="score">{c.count}</div>
              <div className="name">{c.name}</div>
            </div>
          );
        })}
      </div>

      <div className="group-title">Operational Overview</div>

      {/* Fleet Status + Trip Performance */}
      <Row gutter={18}>
        <Col xs={24} lg={6}>
          <div className="card" style={{ height: "100%" }}>
            <div className="section-title">Fleet Status</div>
            <div className="section-sub">Live vehicle status distribution</div>
            <Spin spinning={fleetLoading}>
            <div style={{ position: "relative", height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={FLEET_HEALTH}
                    dataKey="value"
                    nameKey="key"
                    innerRadius={62}
                    outerRadius={90}
                    paddingAngle={0}
                  >
                    {FLEET_HEALTH.map((f) => (
                      <Cell key={f.key} fill={f.color} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%,-50%)",
                  textAlign: "center",
                }}
              >
                <div
                  style={{ fontSize: 24, fontWeight: 700, color: "#1E293B" }}
                >
                  {totalVehicles}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#64748B",
                    letterSpacing: 0,
                  }}
                >
                  VEHICLES
                </div>
              </div>
            </div>
            </Spin>
            <div className="fleet-health-legend">
              {FLEET_HEALTH.map((f) => (
                <div className="fh-row" key={f.key}>
                  <span className="fh-name">
                    <span
                      className="legend-dot"
                      style={{ background: f.color }}
                    />
                    {f.key}
                  </span>
                  <span className="fh-nums">
                    <span className="fh-count">{f.value}</span>
                    <span className="fh-pct">{f.pct.toFixed(1)}%</span>
                  </span>
                </div>
              ))}
            </div>
            {/* <div className="util-bar">
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span>Fleet Utilization Rate</span>
                <strong>{utilizationRate}%</strong>
              </div>
              <div className="bar-bg">
                <div className="bar-fill" style={{ width: `${utilizationRate}%` }} />
              </div>
            </div> */}
          </div>
        </Col>

        <Col xs={24} lg={18}>
          <div className="card" style={{ height: "100%" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <div>
                <div className="section-title">Trip Performance</div>
                <div className="section-sub" style={{ marginBottom: 0 }}>
                  Trip volume and throughput
                </div>
              </div>
              <div className="range-toggle">
                <button
                  className={tripRange === "today" ? "active" : ""}
                  onClick={() => setTripRange("today")}
                >
                  Today
                </button>
                <button
                  className={tripRange === "7d" ? "active" : ""}
                  onClick={() => setTripRange("7d")}
                >
                  Last 7 Days
                </button>
                <button
                  className={tripRange === "30d" ? "active" : ""}
                  onClick={() => setTripRange("30d")}
                >
                  Last 30 Days
                </button>
              </div>
            </div>

            <div className="trip-perf-stats" style={{ marginTop: 16 }}>
              <div className="stat-tile">
                <div className="stat-value">{tripPerf.total}</div>
                <div className="stat-label">Total Trips</div>
              </div>
              <div className="stat-tile">
                <div className="stat-value">{tripPerf.completed}</div>
                <div className="stat-label">Trips Completed</div>
              </div>
              <div className="stat-tile">
                <div className="stat-value">{tripPerf.inProgress}</div>
                <div className="stat-label">Trips In Progress</div>
              </div>
              <div className="stat-tile">
                <div className="stat-value">
                  {formatDuration(tripPerf.avgDurationMins)}
                </div>
                <div className="stat-label">Avg Trip Duration</div>
              </div>
            </div>

            <div style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={tripTrendData}>
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickFormatter={(d) => dayjs(d).format("DD/M")}
                  />
                  {/* <YAxis tick={{ fontSize: 11 }} /> */}
                  <Tooltip />
                  <Bar
                    dataKey="completed"
                    name="Completed"
                    stackId="a"
                    fill="#80C08B"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="inProgress"
                    name="In Progress"
                    stackId="a"
                    fill="#E4A400"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Col>
      </Row>

      <div className="group-title">Attention &amp; Rankings</div>

      {/* Attention Required + Leaderboards */}
      <Row gutter={18}>
        <Col xs={24} lg={6}>
          <div className="card" style={{ height: "100%" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div className="section-title" style={{ marginBottom: 0 }}>
                Attention Required{" "}
                <span
                  style={{
                    background: "#f0f1f4",
                    color: "#5f6d8c",
                    fontSize: 11,
                    borderRadius: 10,
                    padding: "1px 7px",
                    marginLeft: 4,
                  }}
                >
                  {attentionCount}
                </span>
              </div>
              <a
                href="#!"
                style={{ fontSize: 12, color: "#2c963d" }}
                onClick={(e) => {
                  e.preventDefault();
                  goToKpiReport();
                }}
              >
                View All→
              </a>
            </div>
            {attentionDisplay.length === 0 && (
              <div className="section-sub" style={{ marginTop: 14 }}>
                No unresolved alerts today
              </div>
            )}
            {attentionDisplay.map((a, i) => (
              <div className="attention-item" key={i}>
                <div className="attention-item-head">
                  <span className="attention-level">{a.level}</span>
                  <a
                    href="#!"
                    className="attention-view"
                    onClick={(e) => {
                      e.preventDefault();
                      goToKpiReport(a.navName);
                    }}
                  >
                    View ›
                  </a>
                </div>
                <div className="attention-desc">{a.title}</div>
              </div>
            ))}
          </div>
        </Col>

        {[
          { id: "vehicles", title: "Top Vehicles", icon: <DashTopVeh /> },
          { id: "drivers", title: "Top Drivers", icon: <DashTopDriv /> },
          { id: "customers", title: "Top Customers", icon: <DashTopCust /> },
        ].map((panel) => (
          <Col xs={24} sm={12} lg={6} key={panel.title}>
            <div className="card" style={{ height: "100%" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div
                  className="section-title"
                  style={{
                    marginBottom: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {panel.icon}
                  {panel.title}
                </div>
                <Select
                  size="small"
                  value={rankRange[panel.id]}
                  onChange={(v) =>
                    setRankRange((p) => ({ ...p, [panel.id]: v }))
                  }
                  bordered={false}
                  style={{ fontSize: 12, color: "#5F6D8C", minWidth: 112 }}
                  // the list is as wide as its longest option, not as the small
                  // selected value, so every option is fully visible
                  popupMatchSelectWidth={false}
                  dropdownStyle={{ minWidth: 150 }}
                  options={RANK_RANGES}
                />
              </div>
              {topN(rankRange[panel.id], panel.id).map((r, i) => (
                <div className="leaderboard-row" key={r.name}>
                  <span>
                    <span className="rank">{i + 1}</span>
                    {r.name}
                  </span>
                  <span className="trips">{r.trips} trips</span>
                </div>
              ))}
            </div>
          </Col>
        ))}
      </Row>

      {/* Business Trends */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          margin: "26px 0 12px",
        }}
      >
        <div
          className="section-title"
          style={{ marginBottom: 0, fontSize: 18 }}
        >
          Business Trends
        </div>
        <span style={{ fontWeight: 700, fontSize: "12px", color: "#2C963D" }}>
          Last 12 months
        </span>
      </div>
      <Row gutter={18}>
        <Col xs={24} lg={showDistanceChart ? 12 : 24}>
          <div className="card">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <div>
                <div className="section-title" style={{ marginBottom: 2 }}>
                  Trips Completed Over Time
                </div>
                <div
                  style={{ fontSize: 22, fontWeight: 700, color: "#1f2430" }}
                >
                  {(lastTrips?.value || 0).toLocaleString()}{" "}
                  <span
                    style={{ fontSize: 13, fontWeight: 400, color: "#98a1b5" }}
                  >
                    trips in {lastTrips?.month || dayjs().format("MMM")}
                  </span>
                </div>
              </div>
              <span className={`trend-badge ${tripsPct >= 0 ? "up" : "down"}`}>
                {tripsPct >= 0 ? "▲" : "▼"} {Math.abs(tripsPct).toFixed(1)}%
              </span>
            </div>
            <div style={{ height: 200, marginTop: 10 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={tripsSeries}
                  margin={{ left: 10, right: 10, top: 10, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="tripsFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2C963D" stopOpacity={1} />
                      <stop offset="100%" stopColor="#2C963D" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11 }}
                    interval={0}
                    padding={{ left: 10, right: 10 }}
                  />
                  <YAxis hide />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="value"
                    name="Trips"
                    isAnimationActive={false}
                    stroke="#2c963d"
                    strokeWidth={2}
                    fill="url(#tripsFill)"
                    fillOpacity={0.4}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Col>
        {showDistanceChart && (
        <Col xs={24} lg={12}>
          <div className="card">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <div>
                <div className="section-title" style={{ marginBottom: 2 }}>
                  Distance Travelled Over Time
                </div>
                <div
                  style={{ fontSize: 22, fontWeight: 700, color: "#1f2430" }}
                >
                  {(lastDistance?.value || 0).toLocaleString()}{" "}
                  <span
                    style={{ fontSize: 13, fontWeight: 400, color: "#98a1b5" }}
                  >
                    km in {lastDistance?.month || dayjs().format("MMM")}
                  </span>
                </div>
              </div>
              <span
                className={`trend-badge ${distancePct >= 0 ? "up" : "down"}`}
              >
                {distancePct >= 0 ? "▲" : "▼"}{" "}
                {Math.abs(distancePct).toFixed(1)}%
              </span>
            </div>
            <div style={{ height: 200, marginTop: 10 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={distanceSeries}
                  margin={{ left: 10, right: 10, top: 10, bottom: 0 }}
                >
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11 }}
                    interval={0}
                    padding={{ left: 10, right: 10 }}
                  />
                  <YAxis hide />
                  <Tooltip />
                  <Bar
                    dataKey="value"
                    name="Distance (km)"
                    fill="#3B82F6CC"
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={false}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Col>
        )}
      </Row>
    </Wrap>
  );
};

export default NexusDashboardAI;
